import React from "react";
import { CoverImage as CoverImageType } from "@src/types.generated";
import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { LinkType } from "@src/lib/utils/files";
import { Button } from "@src/lib/ui/Button";
import BucketPaintOutlineIcon from "@src/lib/icons/18px/bucket-paint-outline";
import CropIcon from "@src/lib/icons/18px/crop";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import PinturaModal from "@src/lib/ui/PinturaModal";

type Props = {
  coverImage: CoverImageType | null | undefined;
  setCoverImage: (coverImage: CoverImageType | undefined | null) => void;
  /**
   * "overlay" (default) floats the controls over the cover and shows them on
   * hover; "row" lays them out in normal flow, always visible — for a small
   * cover that has no room to hide anything under a pointer.
   */
  layout?: "overlay" | "row";
};

const UpdateCoverImageMenu: React.FC<Props> = (props) => {
  const { layout = "overlay" } = props;
  const overlay = layout === "overlay";
  const buttonSize = overlay ? "default" : "sm";
  const iconButtonSize = overlay ? "icon" : "icon-sm";
  const buttonClassName = overlay ? "hidden group-hover:flex" : undefined;
  const [showPinturaModal, setShowPinturaModal] = React.useState(false);
  const fullSizeCoverImage = React.useMemo(
    () =>
      props.coverImage?.imageUrls?.medium ||
      props.coverImage?.imageUrls?.original ||
      props.coverImage?.imageUrls?.wide,
    [props.coverImage],
  );
  return (
    <TooltipProvider>
      <div
        className={
          overlay
            ? "group absolute inset-0 flex items-end justify-end gap-2 p-2"
            : "flex items-center gap-1.5"
        }
      >
        <AddMediaDialog
          title="Add media"
          onMediaChange={(x) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              emojiIcon: null,
              background: null,
              mediaUrl: x.mediaUrls.en,
              imageUrls: {
                id: props.coverImage?.id || "",
                medium: x.mediaUrls.en,
                wide: x.mediaUrls.en,
                original: x.mediaUrls.en || "",
              },
            });
          }}
          onResourceChange={(trainingResource) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              emojiIcon: null,
              background: null,
              mediaUrl: trainingResource.publishedVersion?.media?.mediaUrls.en,
              imageUrls: {
                id: props.coverImage?.id || "",
                medium: trainingResource.publishedVersion?.media?.mediaUrls.en,
                wide: trainingResource.publishedVersion?.media?.mediaUrls.en,
                original:
                  trainingResource.publishedVersion?.media?.mediaUrls.en || "",
              },
            });
          }}
          onCoverImageChange={(x) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              emojiIcon: x.emojiIcon || props.coverImage?.emojiIcon,
              background: x.background || props.coverImage?.background,
            });
          }}
          initialCoverImage={props.coverImage || undefined}
          filterResources={[LinkType.IMAGE, LinkType.EMOJI]}
          trigger={
            <Button
              variant="outline"
              size={buttonSize}
              className={buttonClassName}
            >
              <BucketPaintOutlineIcon
                data-icon={overlay ? undefined : "inline-start"}
                className="size-4"
              />
              Change cover
            </Button>
          }
        />
        {props.coverImage?.imageUrls ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size={iconButtonSize}
                  variant="outline"
                  aria-label="Edit image"
                  onClick={() => setShowPinturaModal(true)}
                  className={buttonClassName}
                >
                  <CropIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit image</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size={iconButtonSize}
                  variant="outline"
                  aria-label="Remove image"
                  className={buttonClassName}
                  onClick={() => props.setCoverImage(undefined)}
                >
                  <TrashOutlineIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove image</TooltipContent>
            </Tooltip>
          </>
        ) : null}
      </div>
      {fullSizeCoverImage && showPinturaModal && props.coverImage?.imageUrls ? (
        <PinturaModal
          aspectRatio={16 / 9}
          onNewImageUrl={(x) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              background: null,
              emojiIcon: null,
              mediaUrl: x,
              imageUrls: {
                id: props.coverImage?.id || "",
                medium: x,
                wide: x,
                original: x,
              },
            });
          }}
          imageUrl={fullSizeCoverImage}
          visible={showPinturaModal}
          hide={() => setShowPinturaModal(false)}
        />
      ) : null}
    </TooltipProvider>
  );
};

export default UpdateCoverImageMenu;
